/**
 * The chime that plays when a new support ticket arrives (t1078).
 *
 * Generated with the Web Audio API rather than shipped as a file — two short
 * notes need no asset, no extra request and no licence. Whether it plays is a
 * per-browser choice kept in localStorage, like the theme: a front-desk
 * computer and a manager's laptop reasonably want different answers.
 */

const SOUND_KEY = 'mv-ticket-alert-sound'

/** One sound per kind of notification (t1411). `ticket` is the original chime. */
export type AlertSoundKind =
  | 'ticket'
  | 'workspace-message'
  | 'missed-call'
  | 'ticket-note'
  | 'flagged-important'
  | 'task-completed'
  | 'task-review'
  | 'task-reply'
  | 'task-sent-back'

export const SOUND_LABELS: Record<AlertSoundKind, string> = {
  ticket: 'New support ticket',
  'workspace-message': 'Workspace message',
  'missed-call': 'Missed call',
  'ticket-note': 'Note on your ticket',
  'flagged-important': 'Marked important for you',
  'task-completed': 'Task completed',
  'task-review': 'Task needs your review',
  'task-reply': 'Reply on your task',
  'task-sent-back': 'Task sent back to you',
}

type Note = [frequency: number, offset: number, duration: number]
const SOUNDS: Record<AlertSoundKind, { wave: OscillatorType; notes: Note[] }> = {
  ticket: {
    wave: 'triangle',
    notes: [
      [880, 0, 0.18],
      [1318.5, 0.16, 0.32],
    ],
  },
  'workspace-message': {
    wave: 'sine',
    notes: [
      [660, 0, 0.12],
      [990, 0.12, 0.12],
    ],
  },
  'missed-call': {
    wave: 'triangle',
    notes: [
      [784, 0, 0.2],
      [523.3, 0.18, 0.35],
    ],
  },
  'ticket-note': { wave: 'sine', notes: [[1046.5, 0, 0.35]] },
  'flagged-important': {
    wave: 'square',
    notes: [
      [587.3, 0, 0.1],
      [740, 0.11, 0.1],
      [880, 0.22, 0.2],
    ],
  },
  'task-completed': {
    wave: 'sine',
    notes: [
      [523.3, 0, 0.14],
      [659.3, 0.13, 0.14],
      [784, 0.26, 0.3],
    ],
  },
  'task-review': {
    wave: 'sine',
    notes: [
      [698.5, 0, 0.12],
      [698.5, 0.2, 0.2],
    ],
  },
  'task-reply': {
    wave: 'sine',
    notes: [
      [587.3, 0, 0.14],
      [880, 0.13, 0.28],
    ],
  },
  'task-sent-back': {
    wave: 'triangle',
    notes: [
      [659.3, 0, 0.16],
      [493.9, 0.15, 0.3],
    ],
  },
}

function storageKey(kind: AlertSoundKind): string {
  // The ticket chime keeps its original key so existing choices survive.
  return kind === 'ticket' ? SOUND_KEY : `mv-alert-sound-${kind}`
}

export function isSoundEnabled(kind: AlertSoundKind): boolean {
  try {
    return localStorage.getItem(storageKey(kind)) !== 'off'
  } catch {
    return true
  }
}

export function setSoundEnabled(kind: AlertSoundKind, enabled: boolean): void {
  try {
    localStorage.setItem(storageKey(kind), enabled ? 'on' : 'off')
  } catch {
    // Private browsing can make storage throw; the choice just won't persist.
  }
}

export function isAlertSoundEnabled(): boolean {
  return isSoundEnabled('ticket')
}

export function setAlertSoundEnabled(enabled: boolean): void {
  setSoundEnabled('ticket', enabled)
}

let context: AudioContext | null = null

function audioContext(): AudioContext | null {
  if (context) return context
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  context = new Ctor()
  return context
}

/**
 * Browsers keep audio locked until the page has had a click or keypress, so
 * the context is created and resumed on the first one. Until then a chime is
 * silently skipped rather than queued — a sound minutes late is misleading.
 * Returns the cleanup for the listeners.
 */
export function armAlertSound(): () => void {
  const unlock = () => {
    void audioContext()?.resume()
    remove()
  }
  const remove = () => {
    window.removeEventListener('pointerdown', unlock)
    window.removeEventListener('keydown', unlock)
  }
  window.addEventListener('pointerdown', unlock)
  window.addEventListener('keydown', unlock)
  return remove
}

function note(
  ctx: AudioContext,
  wave: OscillatorType,
  frequency: number,
  start: number,
  duration: number,
): void {
  const oscillator = ctx.createOscillator()
  const gain = ctx.createGain()
  oscillator.type = wave
  oscillator.frequency.value = frequency
  gain.gain.setValueAtTime(0.0001, start)
  gain.gain.exponentialRampToValueAtTime(wave === 'square' ? 0.12 : 0.25, start + 0.02)
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration)
  oscillator.connect(gain).connect(ctx.destination)
  oscillator.start(start)
  oscillator.stop(start + duration + 0.05)
}

/** `force` is for the settings screen's "Play a test sound", which should
 *  sound even while the sound option is off. */
export async function playAlertSound(
  kind: AlertSoundKind,
  options: { force?: boolean } = {},
): Promise<void> {
  if (!options.force && !isSoundEnabled(kind)) return
  const ctx = audioContext()
  if (!ctx) return
  if (ctx.state !== 'running') {
    try {
      await ctx.resume()
    } catch {
      return
    }
  }
  if (ctx.state !== 'running') return
  const start = ctx.currentTime
  const { wave, notes } = SOUNDS[kind]
  for (const [frequency, offset, duration] of notes)
    note(ctx, wave, frequency, start + offset, duration)
}

export function playAlertChime(options: { force?: boolean } = {}): Promise<void> {
  return playAlertSound('ticket', options)
}
